import type { GetStaticProps, NextPage } from 'next';
import { useDeferredValue, useMemo, useState } from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { SSRConfig, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import get from 'lodash/get';
import { AxiosResponseHeaders } from 'axios';
import dynamic from 'next/dynamic';
import upperFirst from 'lodash/upperFirst';
import Image from 'next/image';
import Marquee from 'react-fast-marquee';
import HeroBanner from '../components/home/HeroBanner';
import MapSelector from '../components/home/MapSelector';
import type NotificationAlertType from '../../types/NotificationAlert';
import type FeaturedHotelType from '../../types/FeaturedHotel';
import type FlagshipHotelType from '../../types/FlagshipHotel';
import type CampaignBannerType from '../../types/CampaignBanner';
import type MagazineBannerType from '../../types/MagazineBanner';
import type HeroBannerType from '../../types/HeroBanner';
import type SupportBannerType from '../../types/SupportBanner';
import type FooterType from '../../types/Footer';
import { RegionEnum } from '../core/japaneseRegions';
import { requestWithCache } from '../core/request';
import { COMMON_DATE_FORMAT, Locale } from '../core/constants';
import HotelCount from '../../types/HotelCount';
import translator from '../core/helpers/translator';
import useHotelSearchConditions from '../components/common/HotelAvailabilitySearch/useHotelSearchConditions';
import { generateLinks } from '../core/helpers/link';
import { useGetPartnershipSettings } from '../core/partnership/use-partnership-settings';
import analytics from '../core/analytics/analytics';
import PageLayout from '../components/common/PageLayout';
import FeaturedHotels from '../components/home/FeaturedHotels';
import FlagshipHotels from '../components/home/FlagshipHotels';
import CampaignBanner from '../components/home/CampaignBanner';
import MagazineBanner from '../components/home/MagazineBanner';
import SupportBanner from '../components/home/SupportBanner';
import PrefecturesList from '../components/home/PrefecturesList';
import useIsSticky from '../core/hooks/useIsSticky';
import useIsAboveBreakpoint from '../core/hooks/useIsAboveBreakpoint';
import styles from '../components/home/style.module.css';
import ContentWrapper from '../components/common/ContentWrapper';
import SNSBanner from '../components/home/SNSBanner/SNSBanner';
import WebSpecialNotices from '../components/home/WebSpecialNotices/WebSpecialNotices';
import WebTopInfo from '../../types/WebTopInfo';
import RegionsList from '../components/home/RegionsList/RegionsList';
import SimpleHotelSearchBar from '../components/home/SimpleHotelSearchBar/SimpleHotelSearchBar';
import HotelAvailabilitySearch from '../components/common/HotelAvailabilitySearch';

const Modal = dynamic(() => import('../components/common/Modal/Modal'));
const MapModalPanel = dynamic(() => import('../components/home/MapModalPanel'));
const AccountDeletedModal = dynamic(() => import('../components/account/AccountDeletedModal'));

type Props = {
  heroBanner: HeroBannerType | undefined;
  campaignBanners: CampaignBannerType[] | undefined;
  webSpecialNotices: CampaignBannerType[] | undefined;
  magazineBanners: MagazineBannerType[] | undefined;
  supportBanner: SupportBannerType | undefined;
  notificationAlert: NotificationAlertType | undefined;
  featuredHotels: FeaturedHotelType[] | undefined;
  flagshipHotels: FlagshipHotelType[] | undefined;
  footer: FooterType | undefined;
  hotelCount: HotelCount | number | undefined;
  locale: string | undefined;
  webTopInfos: WebTopInfo[] | undefined;
} & Metadata &
  SSRConfig;

const Home: NextPage<Props> = ({
  webSpecialNotices,
  magazineBanners,
  supportBanner,
  notificationAlert,
  footer,
  hotelCount,
  webTopInfos,
  ...props
}) => {
  const { t } = useTranslation('home');
  const [childrenOffsetTop, setChildrenOffsetTop] = useState(0);
  const [ref, isSticky] = useIsSticky<HTMLDivElement>({
    offset: childrenOffsetTop,
  });
  const isAboveMDBreakpoint = useIsAboveBreakpoint('MD');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState(RegionEnum.Kanto); // This is starting to smell like a context or redux is necessary...
  const router = useRouter();
  const affiliateId = router.query.affiliate_id as string;
  const { 'account-deleted': deleted } = router.query as { 'account-deleted': string };
  const [isAccountDeleted, setIsAccountDeleted] = useState(Boolean(deleted));
  const availability = useHotelSearchConditions();

  const {
    searchTerm,
    setSearchTerm,
    checkInAndOutDates,
    roomAndGuestCount,
    setCheckInAndOutDates,
    setRoomAndGuestCount,
    guestCountString,
  } = useDeferredValue(availability);
  const checkInDate = checkInAndOutDates.checkInDate?.format(COMMON_DATE_FORMAT);
  const checkOutDate = checkInAndOutDates.checkOutDate?.format(COMMON_DATE_FORMAT);

  const searchQuery = `checkInDate=${checkInDate}&checkOutDate=${checkOutDate}&guestPerRoom=${guestCountString}&roomCount=${roomAndGuestCount.roomCount.toString()}`;

  const { data: partnershipSettings, isInitialLoading: isPartnershipSettingsLoading } =
    useGetPartnershipSettings();

  const featuredHotels = useMemo(() => {
    if (process.env.NEXT_PUBLIC_FEATURE_FLAG_PARTNERSHIP_PARAMETER !== 'true')
      return props.featuredHotels;

    if (!affiliateId) return props.featuredHotels;
    if (isPartnershipSettingsLoading) return [];
    if (!partnershipSettings?.featuredHotels) return [];

    if (partnershipSettings.featuredHotels.length === 0) {
      const whitelistProperties = partnershipSettings.whitelistPropertyIds || [];
      const blacklistProperties = partnershipSettings.blacklistPropertyIds || [];

      return props.featuredHotels?.filter(
        (hotel) =>
          (!whitelistProperties.length ||
            whitelistProperties.some((property) => property.propertyId === hotel.id)) &&
          !blacklistProperties.some((property) => property.propertyId === hotel.id)
      );
    }

    return partnershipSettings.featuredHotels;
  }, [affiliateId, isPartnershipSettingsLoading, partnershipSettings, props.featuredHotels]);

  const campaignBanners = useMemo(() => {
    if (process.env.NEXT_PUBLIC_FEATURE_FLAG_PARTNERSHIP_PARAMETER !== 'true')
      return props.campaignBanners;

    const { showCampaignBanners } = partnershipSettings || {};

    if (!affiliateId) return props.campaignBanners;
    if (isPartnershipSettingsLoading) return [];
    if (showCampaignBanners === false) return [];

    return props.campaignBanners;
  }, [affiliateId, props.campaignBanners, isPartnershipSettingsLoading, partnershipSettings]);

  const events = {
    handleMapRegionSelection: (type: string, value: number) => {
      if (type === 'mobile') {
        setSelectedArea(value);
        setIsModalOpen(true);
      }
    },
    onCloseAccountDeletedModal: () => {
      setIsAccountDeleted(false);
      const parameters = new URLSearchParams(router.query as Record<string, string>);
      parameters.delete('account-deleted');
      router.replace({ pathname: router.pathname, query: parameters.toString() }, undefined, {
        shallow: true,
      });
    },
    openSearchMap: (area: string) => {
      setIsModalOpen(false);
      analytics.google.ecommerce.track.map.viewFromTopPage({
        area: upperFirst(area),
        guestCount: guestCountString,
        roomCount: roomAndGuestCount.roomCount,
        checkInDate,
        checkOutDate,
      });
    },
  };

  const hotelSearchBar = (
    <div
      className={classNames(
        'space-y-4 h-auto bg-white mx-auto lg:flex lg:flex-col lg:space-y-6 transition-all p-2 shadow-md rounded-[32px] lg:rounded-full',
        {
          'border-[1px] border-gray-200 md:shadow-none': isSticky,
          'md:shadow-red': !isSticky,
        }
      )}
      ref={ref}
    >
      <HotelAvailabilitySearch
        id="hero-banner-search"
        size="large"
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        checkInAndOutDates={checkInAndOutDates}
        setCheckInAndOutDates={setCheckInAndOutDates}
        roomAndGuestCount={roomAndGuestCount}
        setRoomAndGuestCount={setRoomAndGuestCount}
        guestCountString={guestCountString}
        roundedSearchBar
      />
    </div>
  );

  return (
    <>
      <PageLayout
        header={{ shadow: !isSticky || (isSticky && !isAboveMDBreakpoint) }}
        footer={{
          content: footer,
        }}
        isNavBarSticky={isSticky}
        hotelCount={hotelCount}
        notificationAlert={notificationAlert}
        setChildrenOffsetTop={setChildrenOffsetTop}
      >
        {isModalOpen && (
          <Modal title="地図から探す" onClose={() => setIsModalOpen(false)}>
            <MapModalPanel selectedArea={selectedArea} onClick={events.openSearchMap} />
          </Modal>
        )}
        {/* Margin offset for the initial position of header assuming only navbar is shown */}
        <main className="relative">
          <div className="relative">
            <HeroBanner heroBanner={props.heroBanner} />
            <div className="absolute -mt-28 h-[236px] w-full overflow-x-clip md:h-[136px]">
              <h1 className="sr-only">{t('catch_copy')}</h1>
              <ContentWrapper className="inset-x-0 mt-8 flex h-36 flex-col items-start justify-between gap-4 md:mt-0 md:flex-row md:items-center md:gap-10">
                <Image
                  className="z-10 md:w-auto"
                  src={
                    router.locale === Locale.JAPANESE ? '/catch-copy-jp.svg' : '/catch-copy-en.svg'
                  }
                  alt={t('catch_copy')}
                  layout="fixed"
                  width={270}
                  height={68}
                />
                {(webTopInfos ?? []).length > 0 && (
                  <Marquee className="z-10 h-11 border-y-2 border-dotted border-gray-600 text-base font-bold text-orange-700">
                    {webTopInfos?.map((info) => (
                      <span key={info.content} className="mx-4">
                        {info.content}
                      </span>
                    ))}
                  </Marquee>
                )}
              </ContentWrapper>
              <div className={`absolute top-0 h-full bg-white ${styles.curve}`} />
            </div>
          </div>

          <div
            className={classNames('z-20 -mt-48 md:sticky md:inset-x-0 md:-mt-64 lg:-mt-56', {
              'md:top-16': !notificationAlert,
            })}
            style={notificationAlert ? { top: `${childrenOffsetTop}px` } : undefined}
            ref={ref}
          >
            <div
              className={classNames('transition-all ease-in h-52 md:h-full', {
                'md:w-full md:z-20 md:bg-white md:shadow-md': isSticky,
              })}
            >
              {/* mobile */}
              <div className="block md:hidden">
                <div
                  className={classNames(
                    'mx-auto max-w-screen-xl sm:px-8 sm-max:px-4 w-full absolute md:hidden'
                  )}
                >
                  <div
                    className={classNames(
                      'space-y-4 h-auto bg-white mx-auto lg:flex lg:flex-col lg:space-y-6 transition-all shadow-red rounded-full'
                    )}
                    ref={ref}
                  >
                    <SimpleHotelSearchBar
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      checkInAndOutDates={checkInAndOutDates}
                      setCheckInAndOutDates={setCheckInAndOutDates}
                      roomAndGuestCount={roomAndGuestCount}
                      setRoomAndGuestCount={setRoomAndGuestCount}
                      guestCountString={guestCountString}
                    />
                  </div>
                </div>
              </div>

              {/* desktop */}
              <div
                className={classNames(
                  'mx-auto max-w-screen-xl sm:px-8 sm-max:px-4 h-[138px] absolute md:static lg:h-20 hidden md:block'
                )}
              >
                {hotelSearchBar}
              </div>
            </div>
          </div>
          {/* desktop */}
          <div className="hidden md:mt-44 md:block">
            <MapSelector
              hotelCount={hotelCount}
              onSelectionChanged={events.handleMapRegionSelection}
              onClickArea={events.openSearchMap}
              searchQuery={searchQuery}
            />
          </div>
          {/* mobile */}
          <div
            className={classNames('md:mt-8', {
              'mt-20': (webTopInfos ?? []).length > 0,
            })}
          >
            <RegionsList searchQuery={searchQuery} />
          </div>
          {campaignBanners && campaignBanners?.length > 0 && (
            <CampaignBanner campaignBanners={campaignBanners} />
          )}
          {props.flagshipHotels && props.flagshipHotels.length > 0 && (
            <FlagshipHotels
              flagshipHotels={props.flagshipHotels}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              guestPerRoom={guestCountString}
              roomCount={roomAndGuestCount.roomCount}
            />
          )}
          {featuredHotels && featuredHotels.length > 0 && (
            <FeaturedHotels
              featuredHotels={featuredHotels}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              guestPerRoom={guestCountString}
              roomCount={roomAndGuestCount.roomCount}
            />
          )}
          <SNSBanner />
          {webSpecialNotices && webSpecialNotices?.length > 0 && (
            <WebSpecialNotices banners={webSpecialNotices} />
          )}
          {magazineBanners && magazineBanners.length > 0 && (
            <MagazineBanner magazineBanners={magazineBanners} />
          )}
          {supportBanner?.desktopImage && supportBanner?.mobileImage && (
            <SupportBanner supportBanner={supportBanner} />
          )}
          <div className="hidden sm:block">
            <PrefecturesList searchQuery={searchQuery} />
          </div>
        </main>
      </PageLayout>
      {isAccountDeleted && (
        <AccountDeletedModal
          isOpen={isAccountDeleted}
          onClose={events.onCloseAccountDeletedModal}
        />
      )}
    </>
  );
};

const revalidate = process.env.REVALIDATE_HOME_PAGE
  ? Number(process.env.REVALIDATE_HOME_PAGE)
  : false;

export const getStaticProps: GetStaticProps<Partial<Props>> = async ({ locale }) => {
  const heroBannersPromise = requestWithCache<HeroBannerType[]>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/hero-banners?locale=${locale}&tabist=ok`,
    method: 'GET',
  });

  const campaignBannersPromise = requestWithCache<CampaignBannerType[]>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/campaign-banners?locale=${locale}&tabist=ok`,
    method: 'GET',
  });

  const webSpecialNoticesPromise = requestWithCache<CampaignBannerType[]>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/web-special-notices?locale=${locale}&tabist=ok`,
    method: 'GET',
  });

  const magazineBannersPromise = requestWithCache<MagazineBannerType[]>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/magazine-banners?locale=${locale}&tabist=ok`,
    method: 'GET',
  });

  const supportBannerPromise = requestWithCache<SupportBannerType>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/support-banner?locale=${locale}&tabist=ok`,
    method: 'GET',
  });

  const featuredHotelPromise = requestWithCache<FeaturedHotelType[]>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/featured-hotels?locale=${locale}&tabist=ok`,
    method: 'GET',
  });

  const flagshipHotelPromise = requestWithCache<FlagshipHotelType[]>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/flagship-hotels?locale=${locale}&tabist=ok`,
    method: 'GET',
  }).catch(() => []);

  const notificationAlertPromise = requestWithCache<NotificationAlertType>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/notification-alert?locale=${translator.locale.get(
      locale
    )}&tabist=ok`,
    method: 'GET',
  });

  const footerPromise = requestWithCache<FooterType>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/footer?locale=${translator.locale.get(
      locale
    )}&tabist=ok`,
    method: 'GET',
  });

  const hotelCountPromise = requestWithCache<AxiosResponseHeaders & { 'x-total-count': number }>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/hotels?locale=${Locale.JAPANESE}&tabist=ok`,
    method: 'HEAD',
  });

  const webTopInfosPromise = requestWithCache<WebTopInfo[]>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/web-top-infos?locale=${translator.locale.get(
      locale
    )}&tabist=ok`,
    method: 'GET',
  });

  const [
    heroBanners,
    campaignBanners,
    webSpecialNotices,
    magazineBanners,
    supportBanner,
    featuredHotels,
    flagshipHotels,
    notificationAlert,
    footer,
    hotelCount,
    webTopInfos,
  ] = await Promise.all([
    heroBannersPromise,
    campaignBannersPromise,
    webSpecialNoticesPromise,
    magazineBannersPromise,
    supportBannerPromise,
    featuredHotelPromise,
    flagshipHotelPromise,
    notificationAlertPromise,
    footerPromise,
    hotelCountPromise,
    webTopInfosPromise,
  ]);

  const ssrConfig = await serverSideTranslations(translator.locale.get(locale), [
    'common',
    'component',
    'page',
    'home',
    'hotel',
  ]);

  const namespaces =
    // eslint-disable-next-line no-underscore-dangle
    locale && ssrConfig._nextI18Next.initialI18nStore[translator.locale.get(locale)];
  const url = new URL(
    (process.env.NEXT_PUBLIC_SITE_URL
      ? process.env.NEXT_PUBLIC_SITE_URL
      : process.env.VERCEL_URL) as string
  );

  const translationTargets = {
    title: get(namespaces.page, 'home.title', null),
    description: get(namespaces.page, 'home.description', null),
  };

  const translatedMetadata = await translator.batch.translate(translationTargets, locale);

  const metadata = {
    title: translatedMetadata.title,
    description: translatedMetadata.description,
    'og:title': translatedMetadata.title,
    'og:description': translatedMetadata.description,
    'og:image': `${url.origin}/tabist.png`,
    'og:locale': locale,
    'og:url': `${url.origin}${locale !== Locale.JAPANESE ? `/${locale}` : ''}`,
  };

  const heroBanner = heroBanners[Math.floor(Math.random() * heroBanners.length)];

  const contents = heroBanner
    ? [
        {
          href: heroBanner.desktopContentImage?.url || '',
          as: 'image',
        },
        {
          href: heroBanner.mobileContentImage?.url || '',
          as: 'image',
        },
      ]
    : [];

  return {
    props: {
      ...ssrConfig,
      heroBanner,
      campaignBanners,
      webSpecialNotices,
      magazineBanners,
      supportBanner,
      notificationAlert,
      featuredHotels,
      flagshipHotels,
      footer,
      locale,
      hotelCount: hotelCount['x-total-count'] && Number(hotelCount['x-total-count']),
      metadata: locale && namespaces && metadata,
      links: generateLinks(url, contents),
      webTopInfos,
    },
    revalidate,
  };
};

export default Home;
